import { consoleEvents, LoadingStates } from '@pb/utils'
import { clone } from 'ramda'
import { createReducer } from 'redux-act'

import { ACCOUNT_ROUTES } from 'App/constants'

import { MENU_ITEM_KEYS, MENU_SECTION_KEYS, NEW_MENU_ITEM_KEYS } from '../constants'
import actions from './actions'
import initialState from './initialState'
import { UserMenuState, MenuItem } from './types'

const setBadge = (state, key): any => ({
  ...state,
  userMenus: state.userMenus.map((userMenu) => ({
    heading: userMenu.heading,
    key: userMenu.key,
    menuItems: userMenu.menuItems.map((userMenuItem) => ({
      ...userMenuItem,
      shouldShowBadge: userMenuItem.key === key || userMenuItem.shouldShowBadge,
    })),
  })),
})

const setDisablement = (state, { key, isDisabled }): any => ({
  ...state,
  userMenus: state.userMenus.map((userMenu) => ({
    heading: userMenu.heading,
    key: userMenu.key,
    menuItems: userMenu.menuItems.map((userMenuItem) => ({
      ...userMenuItem,
      isDisabled: userMenuItem.key === key ? isDisabled : userMenuItem.isDisabled,
    })),
  })),
})

const setVisibility = (state, { key, isVisible }): any => ({
  ...state,
  userMenus: state.userMenus.map((userMenu) => ({
    heading: userMenu.heading,
    key: userMenu.key,
    menuItems: userMenu.menuItems.map((userMenuItem) => ({
      ...userMenuItem,
      isVisible: userMenuItem.key === key ? isVisible : userMenuItem.isVisible,
    })),
  })),
})

const updateUserMenuState = (state, newUserMenus: MenuItem[]): UserMenuState => ({
  ...state,
  newUserMenus,
})

const fetchReviewItemsNumberStart = (state): any => ({
  ...state,
  reviewItems: {
    ...state.reviewItems,
    isLoading: true,
  },
})

const fetchReviewItemsNumberSuccess = (state, number): any => ({
  ...state,
  reviewItems: {
    ...state.reviewItems,
    isLoading: false,
    isLoadingFailed: false,
    isRequested: true,
    number,
  },
  userMenus: state.userMenus.map((userMenu) => ({
    heading: userMenu.heading,
    key: userMenu.key,
    menuItems: userMenu.menuItems.map((userMenuItem) => ({
      ...userMenuItem,
      number: userMenuItem.key === MENU_ITEM_KEYS.reviewItems ? number : null,
    })),
  })),
  newUserMenus: state.newUserMenus.map((menuItem) => ({
    ...menuItem,
    number: menuItem.key === NEW_MENU_ITEM_KEYS.review ? number : null,
  })),
})

const fetchReviewItemsNumberFail = (state, error): any => {
  consoleEvents.error('Failed to fetch review items number', error)

  return {
    ...state,
    reviewItems: {
      ...state.reviewItems,
      isLoading: false,
      isLoadingFailed: true,
      isRequested: true,
    },
  }
}

const addMenuSection = (state, { sectionHeading, sectionKey, sectionIndex }): any => {
  if (sectionHeading && Number.isInteger(sectionIndex)) {
    const userMenus = clone(state.userMenus)
    const existingMenuSection = userMenus.find((section) => section.key === sectionKey)
    if (!existingMenuSection) {
      const newSection = { heading: sectionHeading, key: sectionKey, menuItems: [] }
      userMenus.splice(sectionIndex, 0, newSection)
      return { ...state, userMenus }
    }
  }
  return { ...state }
}

const addMenuItem = (state, { sectionKey, itemIndex, item }): any => {
  if (sectionKey && item && Number.isInteger(itemIndex)) {
    const userMenus = clone(state.userMenus)
    const menuSectionIndex = userMenus?.findIndex((section) => section.key === sectionKey)
    const existingMenuItem = userMenus[menuSectionIndex]?.menuItems?.find(
      (menuItem) => menuItem.key === item.key,
    )
    if (!existingMenuItem && userMenus) {
      userMenus[menuSectionIndex]?.menuItems?.splice(itemIndex, 0, item)
      return { ...state, userMenus }
    }
  }
  return { ...state }
}

const addDigitalStylistQuiz = (state, featureToggleFlags): any => {
  if (featureToggleFlags && featureToggleFlags.isDigitalStylistEnabled) {
    const heading = 'account.my.services.title'
    const digitalStylistMenuSection = {
      sectionHeading: heading,
      sectionIndex: 1,
      sectionKey: MENU_SECTION_KEYS.services,
    }
    const digitalStylistMenuItem = {
      sectionKey: MENU_SECTION_KEYS.services,
      itemIndex: 0,
      item: {
        key: MENU_ITEM_KEYS.digitalStylist,
        title: 'account.my.services.digitalstylist',
        isDisabled: false,
        isVisible: true,
        isRoute: true,
        to: ACCOUNT_ROUTES.DigitalStylist.path,
        shouldShowBadge: false,
        icon: '',
      },
    }
    const newState = addMenuSection(state, digitalStylistMenuSection)
    return addMenuItem(newState, digitalStylistMenuItem)
  }
  return { ...state }
}

const addInviteFriendMenuItem = (state): any => {
  const inviteFriendMenuItem = {
    sectionKey: MENU_SECTION_KEYS.account,
    itemIndex: 1,
    item: {
      key: MENU_ITEM_KEYS.inviteFriendPage,
      title: ACCOUNT_ROUTES.InviteFriend.title,
      isDisabled: false,
      isVisible: true,
      isRoute: true,
      to: ACCOUNT_ROUTES.InviteFriend.path,
      shouldShowBadge: false,
      icon: '',
    },
  }
  return addMenuItem(state, inviteFriendMenuItem)
}

const fetchUserMenuStart = (state): UserMenuState => ({
  ...state,
  loadingState: LoadingStates.Loading,
})

const fetchUserMenuSuccess = (state, newUserMenus): UserMenuState => ({
  ...state,
  newUserMenus,
  loadingState: LoadingStates.LoadedSuccess,
})

const fetchUserMenuFail = (state): UserMenuState => ({
  ...state,
  loadingState: LoadingStates.LoadedFail,
})

const reducer = createReducer((on) => {
  on(actions.setBadge, setBadge)
  on(actions.setDisablement, setDisablement)
  on(actions.setVisibility, setVisibility)
  on(actions.updateUserMenuState, updateUserMenuState)
  on(actions.fetchReviewItemsNumberStart, fetchReviewItemsNumberStart)
  on(actions.fetchReviewItemsNumberSuccess, fetchReviewItemsNumberSuccess)
  on(actions.fetchReviewItemsNumberFail, fetchReviewItemsNumberFail)
  on(actions.addDigitalStylistQuiz, addDigitalStylistQuiz)
  on(actions.addInviteFriendMenuItem, addInviteFriendMenuItem)
  on(actions.fetchUserMenuStart, fetchUserMenuStart)
  on(actions.fetchUserMenuSuccess, fetchUserMenuSuccess)
  on(actions.fetchUserMenuFail, fetchUserMenuFail)
}, initialState)

export default reducer
